import "../chat-agent/ChatPage.scss";
import type React from "react";
import {useEffect, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import {Button, IconButton} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import {useStores} from "../../stores";
import {useChatContext} from "../chat-agent/context/ChatContext";
import {ChatArea} from "../chat-agent/components/ChatArea/ChatArea";
import type {EmbeddedDashboardView} from "../chat-agent/types/chatTypes";

export type AtlasPanelMode =
    | {kind: "new"}
    | {kind: "edit"; sessionId: string};

type Props = {
    mode: AtlasPanelMode;
    onClose: () => void;
    /** Atlas created or changed a dashboard in this conversation. */
    onDashboardChanged?: (dashboard: EmbeddedDashboardView) => void;
    /** "Open dashboard" on the ready banner (new mode only). */
    onOpenDashboard?: (dashboard: EmbeddedDashboardView) => void;
};

const dashboardKey = (d: EmbeddedDashboardView | null) => (d ? `${d.uuid}:${d.refreshToken}` : null);

/**
 * Right-side Atlas panel of the Dashboards module (Mithra 3.0 "Edit with Atlas"): the regular chat,
 * docked next to the gallery or a dashboard. New mode starts a fresh Atlas chat; edit mode restores
 * the conversation that built the dashboard.
 */
export const AtlasDashboardPanel: React.FC<Props> = observer(({mode, onClose, onDashboardChanged, onOpenDashboard}) => {
    const {bagStore} = useStores();
    const {
        embeddedDashboard, isRestoringSession, restoreSession, setChatDocked, startNewChat,
    } = useChatContext();
    const [ready, setReady] = useState<EmbeddedDashboardView | null>(null);
    // Last dashboard state already reported; undefined = take the first one seen after a restore.
    const baseline = useRef<string | null | undefined>(undefined);
    const startedFor = useRef<string | null>(null);
    const modeKey = mode.kind === "new" ? "new" : `edit:${mode.sessionId}`;

    // The panel's chat never takes over the URL (that would navigate to the chat page).
    useEffect(() => {
        setChatDocked(true);
        return () => setChatDocked(false);
    }, [setChatDocked]);

    useEffect(() => {
        if (startedFor.current === modeKey) return;
        startedFor.current = modeKey;
        setReady(null);
        if (mode.kind === "new") {
            // Whatever the context still shows belongs to an earlier chat: only a different one is news.
            baseline.current = dashboardKey(embeddedDashboard);
            // Fresh Atlas chat; the session is created with the first message.
            startNewChat(undefined, bagStore.bagId);
        } else {
            baseline.current = undefined;
            void restoreSession(mode.sessionId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- start once per mode
    }, [modeKey]);

    useEffect(() => {
        if (isRestoringSession) return;
        const key = dashboardKey(embeddedDashboard);
        if (baseline.current === undefined) {
            baseline.current = key;
            return;
        }
        if (!embeddedDashboard || key === baseline.current) return;
        baseline.current = key;
        onDashboardChanged?.(embeddedDashboard);
        if (mode.kind === "new") setReady(embeddedDashboard);
    }, [embeddedDashboard, isRestoringSession, mode.kind, onDashboardChanged]);

    const title = mode.kind === "new" ? "Create with Atlas" : "Edit with Atlas";
    const caption = mode.kind === "new"
        ? "Describe the dashboard you need. It is saved under Yours."
        : "Changes apply straight away to this dashboard.";

    return (
        <aside className="dsh-root dsh-atlas-panel" aria-label={title}>
            <header className="dsh-atlas-head">
                <span className="dsh-atlas-icon" aria-hidden><AutoAwesomeIcon sx={{fontSize: 18}}/></span>
                <div className="dsh-atlas-titles">
                    <div className="dsh-t-panel-title">{title}</div>
                    <div className="dsh-t-caption">{caption}</div>
                </div>
                <IconButton size="small" aria-label="Close Atlas panel" onClick={onClose}>
                    <CloseIcon fontSize="small"/>
                </IconButton>
            </header>
            {ready && (
                <div className="dsh-atlas-ready" role="status">
                    <span>✓ Dashboard ready: <strong>{ready.title}</strong></span>
                    {onOpenDashboard && (
                        <Button size="small" variant="contained" onClick={() => onOpenDashboard(ready)}>
                            Open dashboard
                        </Button>
                    )}
                </div>
            )}
            <div className="chat-page dsh-atlas-chat">
                <ChatArea/>
            </div>
        </aside>
    );
});
